import {useMemo, useCallback} from '@wordpress/element';
import {registerPaymentMethod} from '@woocommerce/blocks-registry';
import {StoreNotice} from '@woocommerce/blocks-checkout';
import {getSetting} from '@woocommerce/settings';
import {CheckoutProvider, PaymentElement, CurrencySelectorElement} from '@stripe/react-stripe-js/checkout';
import {PaymentMethod, PaymentMethodLabel} from "@wc-stripe/blocks-components";
import {initStripe as loadStripe} from '@wc-stripe/blocks-utils';
import SavedCardComponent from "../saved-card-component";
import {useProcessCheckoutError} from "../hooks";
import {useCheckoutSessionPayment} from "./hooks/use-checkout-session-payment";
import {useSyncCheckoutSession} from "./hooks/use-sync-checkout-session";

const data = getSetting('stripe_upm_data');

const PaymentMethodContent = (
    {
        billing,
        components,
        emitResponse,
        eventRegistration,
        shouldSavePayment,
        activePaymentMethod
    }) => {
    const name = data.name;
    const gatewayId = data.gatewayId;
    const {LoadingMask} = components;
    const {billingAddress, cartTotal, currency} = billing;
    const {onCheckoutFail} = eventRegistration;
    const {noticeContexts} = emitResponse;

    const {setPaymentMethodType} = useCheckoutSessionPayment({
        name,
        gatewayId,
        emitResponse,
        billingAddress,
        eventRegistration,
        shouldSavePayment,
        activePaymentMethod
    });

    const {syncing} = useSyncCheckoutSession({gatewayId, cartTotal, currency, shouldSavePayment});

    useProcessCheckoutError({
        emitResponse,
        subscriber: onCheckoutFail,
        messageContext: noticeContexts.PAYMENTS
    });

    const onPaymentElementChange = useCallback((event) => {
        if (event?.value?.type) {
            setPaymentMethodType(event.value.type);
        }
    }, [setPaymentMethodType]);

    // Memoized so an unrelated re-render (e.g. `syncing` toggling) doesn't hand PaymentElement a
    // new options reference and trigger a wasted update() on the underlying element.
    const options = useMemo(() => ({
        fields: {
            billingDetails: {
                address: 'never',
                name: 'never',
                email: 'never',
                phone: billingAddress?.phone ? 'never' : 'auto'
            }
        },
        wallets: {applePay: 'never', googlePay: 'never'},
        ...data.paymentElementOptions
    }), [billingAddress?.phone]);

    const currencySelector = <CurrencySelectorElement/>;
    const paymentElement = <PaymentElement options={options} onChange={onPaymentElementChange}/>;
    const below = data.currencySelectorPosition === 'below_payment_methods';

    return (
        <LoadingMask isLoading={syncing}>
            {below ? paymentElement : currencySelector}
            {below ? currencySelector : paymentElement}
        </LoadingMask>
    )
}

const UniversalPaymentMethod = ({data, ...props}) => {
    const {cartData} = props;
    const {extensions} = cartData;
    // checkoutSession is only added server-side when session creation succeeds - falls back to
    // {} here (not just relying on the || short-circuit below) so a missing/errored session
    // never reaches CheckoutProvider with an invalid clientSecret.
    const session = extensions.stripe_upm?.checkoutSession || {};

    const options = useMemo(() => ({
        clientSecret: session.client_secret,
        adaptivePricing: {allowed: true}
    }), [session.client_secret]);

    if (!session.client_secret) {
        const message = getSetting('stripeErrorMessages').payment_unavailable;
        return (
            <StoreNotice
                status={'warning'}
                isDismissible={false}>
                {message}
            </StoreNotice>
        )
    }

    return (
        <CheckoutProvider stripe={loadStripe} options={options}>
            <PaymentMethodContent {...props}/>
        </CheckoutProvider>
    )
}

registerPaymentMethod({
    name: data.name,
    label: <PaymentMethodLabel
        title={data.title}
        paymentMethod={data.name}
        icons={data.icons}/>,
    ariaLabel: 'Credit Cards',
    canMakePayment: () => true,
    content: <PaymentMethod content={UniversalPaymentMethod} data={data}/>,
    savedTokenComponent: <SavedCardComponent data={data}/>,
    edit: <PaymentMethod content={UniversalPaymentMethod} data={data}/>,
    supports: {
        showSavedCards: data.showSavedCards,
        showSaveOption: data.showSaveOption,
        features: data.features
    }
})